import React, { useState, useRef, useEffect } from 'react';
import { Zap, Database, ArrowDownCircle, ArrowUpCircle, Plus, X, ArrowLeft, Copy, Check } from 'lucide-react';

/**
 * EditCard Component — defined at module level so React keeps a stable reference.
 * Three views: EDIT → LOADING → RESULT
 * Draft state is lifted to parent (draftNodeValues) so it survives remounts.
 */
const EditCard = ({ index, editedNodes, validationStatus, isValidatingNode, isDarkMode, onNodeEdit, onNodeValidate, onApplySuggestion, draftNodeValues, onDraftChange }) => {
  const draftAlias = draftNodeValues?.alias || '';
  const draftDescription = draftNodeValues?.description || '';
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const wasValidating = useRef(isValidatingNode);

  // Auto-scroll to card when it mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // When validation completes (isValidatingNode goes from true→false), flip to result view
  useEffect(() => {
    if (wasValidating.current && !isValidatingNode && validationStatus) {
      setShowResult(true);
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
    wasValidating.current = isValidatingNode;
  }, [isValidatingNode, validationStatus]);

  const handleCopySuggestion = () => {
    if (!validationStatus?.suggestion) return;
    const text = `${validationStatus.suggestion.alias}\n${validationStatus.suggestion.description}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Determine which view to show:
  // LOADING: isValidatingNode is true
  // RESULT: showResult is true and we have validationStatus
  // EDIT: everything else
  const currentView = isValidatingNode ? 'loading' : (showResult && validationStatus) ? 'result' : 'edit';

  return (
    <div
      ref={cardRef}
      className={`node-edit-card absolute top-full mt-2 left-1/2 -translate-x-1/2 z-30 rounded-lg shadow-xl ${
        isDarkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-gray-200'
      }`}
      style={{ width: '340px' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* LOADING VIEW — skeleton */}
      {currentView === 'loading' && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Validating...</span>
            <div className={`animate-pulse rounded w-16 h-5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
          <div className="space-y-3">
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-50 border border-gray-100'}`}>
              <div className={`animate-pulse rounded h-3 w-full mb-2 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              <div className={`animate-pulse rounded h-3 w-4/5 mb-2 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              <div className={`animate-pulse rounded h-3 w-3/5 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            </div>
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-50 border border-gray-100'}`}>
              <div className={`animate-pulse rounded h-3 w-20 mb-2 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              <div className={`animate-pulse rounded h-3 w-full mb-2 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              <div className={`animate-pulse rounded h-3 w-2/3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            </div>
          </div>
        </div>
      )}

      {/* RESULT VIEW */}
      {currentView === 'result' && (
        <div className="p-4">
          {/* Header with back button */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setShowResult(false)}
              className={`flex items-center space-x-1 text-xs font-medium transition-colors ${
                isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to edit</span>
            </button>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              validationStatus.valid
                ? 'bg-green-500/20 text-green-400'
                : 'bg-amber-500/20 text-amber-400'
            }`}>
              {validationStatus.valid ? 'Valid' : 'Needs work'}
            </span>
          </div>

          {/* Explanation */}
          <div className={`p-3 rounded-lg text-sm leading-relaxed ${
            validationStatus.valid
              ? isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-100'
              : isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-100'
          }`}>
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
              {validationStatus.explanation}
            </p>
          </div>

          {/* Suggestion block */}
          {validationStatus.suggestion && (
            <div className={`mt-3 p-3 rounded-lg ${
              isDarkMode ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-100'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                  Suggested edit
                </span>
                <button
                  onClick={handleCopySuggestion}
                  className={`flex items-center space-x-1 text-xs transition-colors ${
                    copied
                      ? 'text-green-400'
                      : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Copy suggestion"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  <span>{copied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {validationStatus.suggestion.alias}
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {validationStatus.suggestion.description}
              </p>
              <button
                onClick={() => {
                  onApplySuggestion?.(index, validationStatus.suggestion);
                  onDraftChange?.({ alias: validationStatus.suggestion.alias, description: validationStatus.suggestion.description });
                  setShowResult(false);
                }}
                className="w-full mt-3 px-3 py-1.5 bg-cyan-500 text-white rounded text-sm font-medium hover:bg-cyan-600 transition-colors"
              >
                Use this suggestion
              </button>
            </div>
          )}

          {/* Re-validate from result view */}
          {!validationStatus.valid && (
            <button
              onClick={() => setShowResult(false)}
              className={`w-full mt-3 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              Edit & re-validate
            </button>
          )}
        </div>
      )}

      {/* EDIT VIEW — draft values stored in parent, survives remounts */}
      {currentView === 'edit' && (
        <div className="p-4">
          {/* Name field */}
          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Name</label>
          <input
            value={draftAlias}
            onChange={(e) => onDraftChange?.({ ...draftNodeValues, alias: e.target.value })}
            className={`w-full px-2 py-1 rounded border text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-[#0077A8] ${
              isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
            }`}
            placeholder="Node name..."
          />

          {/* Description field */}
          <label className={`text-xs font-medium mt-3 block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Description</label>
          <textarea
            value={draftDescription}
            onChange={(e) => onDraftChange?.({ ...draftNodeValues, description: e.target.value })}
            className={`w-full px-2 py-1.5 rounded border text-sm mt-1 h-24 resize-none focus:outline-none focus:ring-1 focus:ring-[#0077A8] ${
              isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
            }`}
            placeholder="What does this node do..."
          />

          {/* Validate button */}
          <button
            onClick={() => onNodeValidate?.(index, draftAlias, draftDescription)}
            disabled={isValidatingNode || !draftAlias.trim() || !draftDescription.trim()}
            className={`w-full mt-3 px-3 py-1.5 bg-[#0077A8] text-white rounded text-sm font-medium hover:bg-[#006090] transition-colors ${
              (isValidatingNode || !draftAlias.trim() || !draftDescription.trim()) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Validate
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * FlowVisualization Component
 * Clean dashed arrows connecting all nodes including AGENT INPUT/OUTPUT
 * Supports edit mode with jiggle animation, inline edit cards, add/remove nodes
 */

const FlowVisualization = ({
  nodes = [],
  workflowSteps = [],
  isDarkMode = false,
  agentDefinition = null,
  variableMappings = null,
  currentNodeIndex = 0,
  onNodeClick = null,
  executionNodes = [], // Array of { status: 'running' | 'completed' | 'failed' }
  // Edit mode props
  isEditing = false,
  editingNodeIndex = null,
  editedNodes = null,
  nodeValidations = {},
  onNodeEdit = null,
  onNodeRemove = null,
  onNodeAdd = null,
  onNodeValidate = null,
  onApplySuggestion = null,
  onEditingNodeChange = null,
  isValidatingNode = false,
  draftNodeValues = null,
  onDraftChange = null,
}) => {
  const containerRef = useRef(null);
  const [hoveredArrow, setHoveredArrow] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Use editedNodes when in edit mode, otherwise use nodes
  const displayNodes = isEditing && editedNodes ? editedNodes.map((n, idx) => ({
    nodeId: `node-${idx}`,
    alias: n.alias,
    description: n.description,
    type: n.type,
  })) : nodes;

  // Close edit card when clicking outside
  useEffect(() => {
    if (!isEditing || editingNodeIndex === null) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !e.target.closest('.node-edit-card') && !e.target.closest('.node-box-clickable')) {
        onEditingNodeChange?.(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, editingNodeIndex, onEditingNodeChange]);

  // Get node schema from agentDefinition
  const getNodeSchema = (nodeIndex) => {
    if (agentDefinition?.nodes?.[nodeIndex]) {
      return {
        input: agentDefinition.nodes[nodeIndex].node_input?.schema || agentDefinition.nodes[nodeIndex].node_input || {},
        output: agentDefinition.nodes[nodeIndex].node_output?.schema || agentDefinition.nodes[nodeIndex].node_output || {}
      };
    }
    return { input: {}, output: {} };
  };

  // Get example values from variableMappings
  const getExampleValue = (fieldName) => {
    if (variableMappings?.variableDict?.[fieldName]) {
      const values = variableMappings.variableDict[fieldName].values;
      return values?.[0] || null;
    }
    return null;
  };

  // Get node details from workflowSteps
  const getNodeDetails = (index) => {
    if (isEditing) {
      // In edit mode, return minimal details
      return { hasIntegration: false, hasAgentOutput: false, hasAgentInput: index === 0, nodeType: 'promptdel', position: index + 1 };
    }
    const step = workflowSteps[index];
    if (!step) return { hasIntegration: false, hasAgentOutput: false, nodeType: null };

    const nodeDetails = step.node?.node_details || {};
    return {
      hasIntegration: nodeDetails.requires_integration || nodeDetails.integration_hint,
      hasAgentOutput: nodeDetails.requires_agent_output || nodeDetails.agent_output_hint,
      hasAgentInput: nodeDetails.requires_agent_input || nodeDetails.agent_input_hint,
      integrationHint: nodeDetails.integration_hint,
      agentInputHint: nodeDetails.agent_input_hint,
      nodeType: step.node?.type,
      businessFunction: step.node?.business_function,
      position: step.position
    };
  };

  // Render tooltip content for arrow hover
  const renderTooltipContent = (schema) => {
    const fields = Object.entries(schema || {});
    if (fields.length === 0) {
      return <span className="text-xs opacity-60">No schema data</span>;
    }
    return (
      <div className="space-y-1">
        {fields.map(([fieldName, fieldType]) => {
          const exampleValue = getExampleValue(fieldName);
          return (
            <div key={fieldName} className="flex flex-col">
              <div className="flex items-center space-x-2">
                <span className={`font-semibold ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                  {fieldName}
                </span>
                <span className={`text-xs px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {fieldType}
                </span>
              </div>
              {exampleValue && (
                <span className={`text-xs italic truncate max-w-[180px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  "{exampleValue}"
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Get label for arrow between nodes
  const getArrowLabel = (fromIndex) => {
    if (isEditing) return { label: '', schema: {} };
    if (fromIndex === -1) {
      // From AGENT INPUT
      const agentInput = agentDefinition?.agent_input?.schema || agentDefinition?.agent_input || {};
      const fields = Object.keys(agentInput);
      return { label: fields.length === 1 ? fields[0] : fields.length > 0 ? `${fields.length} fields` : '', schema: agentInput };
    }
    const schema = getNodeSchema(fromIndex);
    const fields = Object.keys(schema.output);
    return { label: fields.length === 1 ? fields[0] : fields.length > 0 ? `${fields.length} fields` : '', schema: schema.output };
  };

  const arrowColor = isDarkMode ? '#6B7280' : '#9CA3AF';
  const arrowColorHover = isDarkMode ? '#06B6D4' : '#0891B2';

  // Sketchy box component - hand-drawn rectangle border with overlapping strokes
  const SketchyBox = ({ children, color, isSelected, className = '', width = 140, height = 90 }) => {
    const selectedColor = '#FBBF24'; // yellow-400
    const strokeColor = isSelected ? selectedColor : color;
    const w = width, h = height;

    // Each stroke pass has different offsets and overshoots
    const strokes = [
      // Pass 1 - slightly offset, overshoots corners
      {
        top: `M -3 3 Q ${w * 0.3} 1, ${w * 0.6} 4 Q ${w * 0.8} 2, ${w + 4} 2`,
        right: `M ${w - 2} -2 Q ${w - 3} ${h * 0.3}, ${w - 1} ${h * 0.6} Q ${w - 2} ${h * 0.8}, ${w - 3} ${h + 3}`,
        bottom: `M ${w + 2} ${h - 2} Q ${w * 0.7} ${h - 1}, ${w * 0.4} ${h - 3} Q ${w * 0.2} ${h - 1}, -2 ${h - 2}`,
        left: `M 3 ${h + 2} Q 1 ${h * 0.7}, 2 ${h * 0.4} Q 1 ${h * 0.2}, 2 -3`,
        opacity: 0.4
      },
      // Pass 2 - different wobble, different overshoot
      {
        top: `M -1 1 Q ${w * 0.25} 3, ${w * 0.5} 1 Q ${w * 0.75} 2, ${w + 2} 3`,
        right: `M ${w - 1} -1 Q ${w - 2} ${h * 0.25}, ${w - 3} ${h * 0.5} Q ${w - 1} ${h * 0.75}, ${w - 2} ${h + 2}`,
        bottom: `M ${w + 1} ${h - 1} Q ${w * 0.75} ${h - 3}, ${w * 0.5} ${h - 1} Q ${w * 0.25} ${h - 2}, -1 ${h - 3}`,
        left: `M 1 ${h + 1} Q 3 ${h * 0.75}, 1 ${h * 0.5} Q 2 ${h * 0.25}, 3 -2`,
        opacity: 0.5
      },
      // Pass 3 - main stroke, slight variation
      {
        top: `M 0 2 Q ${w * 0.35} 1, ${w * 0.5} 2 Q ${w * 0.65} 1, ${w} 2`,
        right: `M ${w - 2} 0 Q ${w - 1} ${h * 0.35}, ${w - 2} ${h * 0.5} Q ${w - 1} ${h * 0.65}, ${w - 2} ${h}`,
        bottom: `M ${w} ${h - 2} Q ${w * 0.65} ${h - 1}, ${w * 0.5} ${h - 2} Q ${w * 0.35} ${h - 1}, 0 ${h - 2}`,
        left: `M 2 ${h} Q 1 ${h * 0.65}, 2 ${h * 0.5} Q 1 ${h * 0.35}, 2 0`,
        opacity: 0.9
      }
    ];

    return (
      <div className={`relative ${className}`}>
        {/* SVG sketchy border overlay */}
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ overflow: 'visible', width: w, height: h }}
          viewBox={`-5 -5 ${w + 10} ${h + 10}`}
        >
          {/* Multiple overlapping stroke passes */}
          {strokes.map((stroke, i) => (
            <g key={i} opacity={stroke.opacity}>
              <path d={stroke.top} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
              <path d={stroke.right} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
              <path d={stroke.bottom} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
              <path d={stroke.left} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
            </g>
          ))}
          {/* Extra corner overshoots for that hand-drawn feel */}
          <path d="M -2 6 L 6 -2" fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
          <path d={`M ${w - 6} -2 L ${w + 2} 6`} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
          <path d={`M ${w + 2} ${h - 6} L ${w - 6} ${h + 2}`} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
          <path d={`M 6 ${h + 2} L -2 ${h - 6}`} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
        </svg>
        {/* Actual content */}
        {children}
      </div>
    );
  };

  if (displayNodes.length === 0) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No workflow nodes to display
      </div>
    );
  }

  // Check if first node has agent input
  const firstNodeDetails = getNodeDetails(0);
  const hasAgentInput = firstNodeDetails.hasAgentInput;

  // Arrow connector component - sketchy hand-drawn style
  const ArrowConnector = ({ id, schema, showAddButton = false, addAfterIndex = -1 }) => {
    const isHovered = hoveredArrow === id;
    const color = isDarkMode ? '#60A5FA' : '#3B82F6';
    const fieldNames = Object.keys(schema || {});
    const displayFields = fieldNames.slice(0, 4);
    const hasMore = fieldNames.length > 4;

    // Generate slightly wobbly path for hand-drawn effect
    const sketchyLine = (x1, y1, x2, y2, seed = 0) => {
      const midX = (x1 + x2) / 2;
      const wobble1 = 1.5 * Math.sin(seed);
      const wobble2 = -1 * Math.cos(seed + 1);
      return `M ${x1} ${y1 + wobble1} Q ${midX * 0.5} ${y1 + wobble2}, ${midX} ${y1 + wobble1 * 0.5} T ${x2} ${y2 + wobble2 * 0.5}`;
    };

    return (
      <div
        className="flex flex-col items-center mx-1 relative"
        style={{ minWidth: '80px', maxWidth: '140px', marginTop: '35px' }}
      >
        {/* Sketchy hand-drawn arrow */}
        <svg width="80" height="20" className="flex-shrink-0" style={{ overflow: 'visible' }}>
          {/* Multiple overlapping strokes for hand-drawn effect */}
          <path
            d={sketchyLine(0, 10, 65, 10, 1)}
            fill="none"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            opacity="0.5"
          />
          <path
            d={sketchyLine(0, 10, 65, 10, 2)}
            fill="none"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            opacity="0.7"
          />
          <path
            d={sketchyLine(0, 10, 65, 10, 0)}
            fill="none"
            stroke={color}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          {/* Sketchy arrowhead - multiple strokes */}
          <path
            d="M 63 5 L 75 10 M 63 15 L 75 10"
            fill="none"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            opacity="0.5"
          />
          <path
            d="M 64 6 L 76 10 M 64 14 L 76 10"
            fill="none"
            stroke={color}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>

        {/* Add node button between arrows in edit mode */}
        {showAddButton && displayNodes.length < 5 && (
          <button
            onClick={(e) => { e.stopPropagation(); onNodeAdd?.(addAfterIndex); }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-sm hover:bg-cyan-600 z-20 shadow-md transition-transform hover:scale-110"
            title="Add node here"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Field names below arrow - pushed down */}
        {fieldNames.length > 0 && (
          <div
            className="text-center cursor-pointer relative"
            style={{ marginTop: '35px' }}
            onMouseEnter={() => setHoveredArrow(id)}
            onMouseLeave={() => setHoveredArrow(null)}
          >
            {displayFields.map((fieldName) => (
              <div
                key={fieldName}
                className={`leading-normal transition-all duration-150 ${isHovered
                  ? `text-[10px] font-medium ${isDarkMode ? 'text-cyan-300' : 'text-cyan-600'}`
                  : `text-[8px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}`}
              >
                {fieldName}
              </div>
            ))}
            {hasMore && (
              <div className={`text-[8px] ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                +{fieldNames.length - 4} more
              </div>
            )}

          </div>
        )}
      </div>
    );
  };

  // Validation status indicator for edit mode
  const getValidationColor = (index) => {
    const v = nodeValidations[index];
    if (!v) return '#3B82F6'; // blue - not validated
    if (v.valid) return '#22C55E'; // green - valid
    return '#F59E0B'; // amber - invalid/warning
  };

  return (
    <div ref={containerRef} className="relative w-full overflow-visible">
      {/* Jiggle animation styles for edit mode */}
      {isEditing && (
        <style>{`
          @keyframes jiggle {
            0%, 100% { transform: rotate(-0.7deg); }
            25% { transform: rotate(0.7deg); }
            50% { transform: rotate(-0.5deg); }
            75% { transform: rotate(0.5deg); }
          }
          .node-jiggle {
            animation: jiggle 0.3s ease-in-out infinite;
          }
        `}</style>
      )}
      <div
        className="overflow-x-auto custom-scrollbar"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: isDarkMode ? '#4B5563 #1F2937' : '#CBD5E1 #F1F5F9',
          paddingBottom: isEditing ? '220px' : '16px' // Extra space for edit cards
        }}
      >
        <div className={`flex items-start justify-center min-w-max px-4 ${isEditing ? 'pt-2' : 'pt-4'}`}>

          {/* AGENT INPUT box */}
          {hasAgentInput && !isEditing && (
            <>
              <div className="flex flex-col items-center">
                <div className={`border-2 border-cyan-500 rounded-lg p-2 text-center min-w-[90px] ${isDarkMode ? 'bg-cyan-900/40' : 'bg-cyan-100'}`}>
                  <ArrowDownCircle className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                  <span className={`text-[10px] font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>AGENT INPUT</span>
                  {firstNodeDetails.agentInputHint?.expected_inputs && (
                    <div className={`mt-0.5 text-[8px] leading-tight ${isDarkMode ? 'text-cyan-400/80' : 'text-cyan-700/80'}`}>
                      {firstNodeDetails.agentInputHint.expected_inputs.slice(0, 2).map(inp => inp.name).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Arrow from AGENT INPUT to first node */}
              <ArrowConnector
                id="arrow-input-0"
                schema={getArrowLabel(-1).schema}
              />
            </>
          )}

          {/* Main nodes */}
          {displayNodes.map((node, index) => {
            const details = getNodeDetails(index);
            const isSelected = index === currentNodeIndex && !isEditing;
            const arrowInfo = getArrowLabel(index);
            const execState = executionNodes[index];
            const isRunning = execState?.status === 'running';
            const isCorrecting = execState?.status === 'correcting';
            const isCompleted = execState?.status === 'completed';
            const isFailed = execState?.status === 'failed';
            const isEditingThisNode = isEditing && editingNodeIndex === index;
            const shouldJiggle = isEditing && editingNodeIndex !== index;
            const validationStatus = nodeValidations[index];

            return (
              <React.Fragment key={node.nodeId || `node-${index}`}>
                {/* Node */}
                <div
                  className={`relative flex flex-col items-center cursor-pointer transition-transform node-box-clickable ${
                    isSelected ? 'scale-105' : isEditing ? '' : 'hover:scale-102'
                  } ${shouldJiggle ? 'node-jiggle' : ''}`}
                  onClick={() => onNodeClick?.(index)}
                  onMouseEnter={() => setHoveredNode(index)}
                  onMouseLeave={() => setHoveredNode(null)}
                  style={(isRunning || isCorrecting) ? {
                    animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  } : {}}
                >
                  {/* Remove button (x) - edit mode only, if more than 1 node */}
                  {isEditing && displayNodes.length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onNodeRemove?.(index); }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs z-20 hover:bg-red-600 shadow-md transition-transform hover:scale-110"
                      title="Remove node"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}

                  {/* Validation indicator - edit mode */}
                  {isEditing && validationStatus && (
                    <div
                      className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-xs z-20 shadow-md ${
                        validationStatus.valid ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
                      }`}
                      title={validationStatus.explanation}
                    >
                      {validationStatus.valid ? '\u2713' : '!'}
                    </div>
                  )}

                  {/* Execution glow ring */}
                  {(isRunning || isCorrecting || isCompleted || isFailed) && (
                    <div
                      className={`absolute inset-0 rounded-2xl pointer-events-none ${
                        isCorrecting ? 'ring-4 ring-amber-400/60' :
                        isRunning ? 'ring-4 ring-cyan-400/60' :
                        isCompleted ? 'ring-3 ring-green-400/50' :
                        'ring-3 ring-red-400/50'
                      }`}
                      style={isCorrecting ? {
                        boxShadow: '0 0 20px rgba(245, 158, 11, 0.4), 0 0 40px rgba(245, 158, 11, 0.2)',
                        animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                      } : isRunning ? {
                        boxShadow: '0 0 20px rgba(34, 211, 238, 0.4), 0 0 40px rgba(34, 211, 238, 0.2)',
                        animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                      } : isCompleted ? {
                        boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)'
                      } : {
                        boxShadow: '0 0 15px rgba(239, 68, 68, 0.3)'
                      }}
                    />
                  )}
                  <SketchyBox
                    color={isEditing ? getValidationColor(index) : isCorrecting ? '#F59E0B' : isRunning ? '#22D3EE' : isCompleted ? '#22C55E' : isFailed ? '#EF4444' : details.hasIntegration ? '#F43F5E' : details.hasAgentOutput ? '#22C55E' : '#3B82F6'}
                    isSelected={isSelected || isEditingThisNode}
                    width={hoveredNode === index ? 180 : 140}
                    height={hoveredNode === index ? 110 : 90}
                    className="transition-all duration-200"
                  >
                    <div
                      className={`p-3 text-center rounded-xl transition-all duration-200 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
                      style={{
                        width: hoveredNode === index ? 180 : 140,
                        height: hoveredNode === index ? 110 : 90
                      }}
                    >
                      <div className="bg-[#0077A8] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center mx-auto mb-1">
                        {details.position || index + 1}
                      </div>
                      <h5 className={`font-semibold mb-0.5 leading-tight px-1 transition-all duration-150 ${hoveredNode === index ? 'text-sm' : 'text-xs truncate'} ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {node.alias || (isEditing ? 'New Node' : `Node ${index + 1}`)}
                      </h5>
                      <p
                        className={`px-1 transition-all duration-150 overflow-hidden ${
                          hoveredNode === index && !isEditing ? 'text-xs' : 'text-[10px] truncate'
                        } ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                        style={{ display: '-webkit-box', WebkitLineClamp: hoveredNode === index && !isEditing ? 3 : 1, WebkitBoxOrient: 'vertical' }}
                      >
                        {details.businessFunction || node.description || (isEditing ? 'Click to edit' : '')}
                      </p>
                      {!isEditing && details.nodeType === 'promptdel' && (
                        <Zap className={`text-blue-400 mx-auto mt-0.5 transition-all duration-150 ${hoveredNode === index ? 'w-4 h-4' : 'w-3 h-3'}`} />
                      )}
                      {!isEditing && hoveredNode === index && details.hasIntegration && (
                        <div className={`text-[10px] mt-1 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
                          Integration: {details.integrationHint?.suggested_source || 'External'}
                        </div>
                      )}
                    </div>
                  </SketchyBox>

                  {/* Inline Edit Card - flips between edit view and validation result */}
                  {isEditingThisNode && editedNodes && (
                    <EditCard
                      key={`edit-card-${index}`}
                      index={index}
                      editedNodes={editedNodes}
                      validationStatus={validationStatus}
                      isValidatingNode={isValidatingNode}
                      isDarkMode={isDarkMode}
                      onNodeEdit={onNodeEdit}
                      onNodeValidate={onNodeValidate}
                      onApplySuggestion={onApplySuggestion}
                      draftNodeValues={draftNodeValues}
                      onDraftChange={onDraftChange}
                    />
                  )}

                  {/* Integration indicator */}
                  {!isEditing && details.hasIntegration && (
                    <div className="mt-1.5 flex flex-col items-center">
                      <div className="h-3 w-0.5 bg-rose-500"></div>
                      <div className={`border border-rose-500 rounded-lg p-1 text-center min-w-[70px] ${isDarkMode ? 'bg-rose-900/40' : 'bg-rose-100'}`}>
                        <Database className="w-3 h-3 text-rose-400 mx-auto" />
                        <span className={`text-[9px] font-medium ${isDarkMode ? 'text-rose-400' : 'text-rose-700'}`}>INTEGRATION</span>
                        <div className={`text-[8px] uppercase ${isDarkMode ? 'text-rose-400/80' : 'text-rose-600/80'}`}>
                          {details.integrationHint?.suggested_source || 'EXT'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow to next node (or to AGENT OUTPUT if last) */}
                {index < displayNodes.length - 1 ? (
                  <ArrowConnector
                    id={`arrow-${index}-${index + 1}`}
                    schema={arrowInfo.schema}
                    showAddButton={isEditing}
                    addAfterIndex={index}
                  />
                ) : !isEditing ? (
                  /* Arrow to AGENT OUTPUT - only in non-edit mode */
                  <>
                    <ArrowConnector
                      id={`arrow-${index}-output`}
                      schema={arrowInfo.schema}
                    />
                    <div className="flex flex-col items-center">
                      <div className={`border-2 border-green-500 rounded-lg p-2 text-center min-w-[90px] ${isDarkMode ? 'bg-green-900/40' : 'bg-green-100'}`}>
                        <ArrowUpCircle className="w-4 h-4 text-green-400 mx-auto mb-1" />
                        <span className={`text-[10px] font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>AGENT OUTPUT</span>
                      </div>
                    </div>
                  </>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default FlowVisualization;
